import React, {Fragment, useEffect, useState} from 'react';

import {PopoverBody} from 'reactstrap';

import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {
  AttachOverlay,
  OverlayProps,
  PopoverForm,
  PopoverHeading,
} from 'app/general/overlays';
import {StyledFooterDIV} from 'app/general/structures/DocSidebarPagePanels';
import {postAsyncJob} from 'app/jobs/postAsyncJob';
import {PERMISSIONS_EXPORT_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

type Props = {
  documentUrl: string;
};

function ExportPermissionsPopover({
  overlayProps,
  documentUrl,
}: {
  overlayProps: OverlayProps;
  documentUrl: string;
}) {
  const actions = useAsyncActions({postAsyncJob});
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [downloadURL, setDownloadURL] = useState<string | undefined>();

  useEffect(() => {
    if (!overlayProps.isOpen) {
      return;
    }

    setStatus('loading');
    setDownloadURL(undefined);

    actions
      .postAsyncJob({
        url: PERMISSIONS_EXPORT_API_URL,
        obj: {document: documentUrl},
        errorText: gt("Couldn't export permissions"),
      })
      .then((userJob) => {
        setDownloadURL(userJob.result.url);
        setStatus('success');
      })
      .catch(() => {
        setStatus('error');
      });
  }, [overlayProps.isOpen, documentUrl]);

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close}) => (
        <Fragment>
          <PopoverHeading>{gt('Export permissions')}</PopoverHeading>
          <PopoverBody>
            {status === 'loading' ? (
              <div>
                <p>
                  {gt(
                    'This could take some seconds, your permissions will appear here when finished'
                  )}
                  .
                </p>
                <p className="text-center">
                  <b>
                    <i className="fa fa-circle-notch fa-spin" />{' '}
                    {gt('Exporting permissions')}...
                  </b>
                </p>
              </div>
            ) : status === 'success' ? (
              <div>
                <p>{gt('Your permissions have been exported.')}</p>
                <p className="text-center">
                  <a className="btn btn-primary" href={downloadURL}>
                    {gt('Download permissions')}
                  </a>
                </p>
              </div>
            ) : (
              <div className="text-danger text-center">
                <p className="font-weight-bold">
                  {gt('There was an error exporting the permissions')}.
                </p>
                <p>{gt('Please try again later')}.</p>
              </div>
            )}
          </PopoverBody>
          <StyledPopoverBottomDIV>
            <button onClick={close} className="btn btn-link btn-sm">
              <i className="fa fa-times" /> {gt('Close')}
            </button>
          </StyledPopoverBottomDIV>
        </Fragment>
      )}
    </PopoverForm>
  );
}

export function ExportPermissionsButton({documentUrl}: Props) {
  return (
    <StyledFooterDIV>
      <AttachOverlay
        trigger="click"
        overlay={({overlayProps}) => (
          <ExportPermissionsPopover
            overlayProps={{...overlayProps, placement: 'top'}}
            documentUrl={documentUrl}
          />
        )}
      >
        {(handlers) => (
          <button className="btn btn-secondary btn-sm btn-block" {...handlers}>
            <i className="fa fa-download" /> {gt('Export Permissions')}
          </button>
        )}
      </AttachOverlay>
    </StyledFooterDIV>
  );
}
